import React, { useState, useEffect, useRef } from 'react';
import { 
  db, 
  doc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  writeBatch
} from '../../firebase';
import { Layout, X, AlertCircle, Save, Loader, Archive } from 'lucide-react';
import ImageUploader from './ImageUploader';
import styles from '../CSS/EditSpaceModal.module.css';

const EditSpaceModal = ({ onClose, space, onUpdate, spaces }) => {
  const spaceType = space.type || 'spaces'; // Por defecto 'spaces' si no tiene tipo
  const [name, setName] = useState(space.name);
  const [location, setLocation] = useState(space.location || ''); // 🆕 Estado para la ubicación
  const [imageUrl, setImageUrl] = useState(space.imageUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Auto-focus the input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);

    // Add escape key listener
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    // Click outside to close
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onClose]);

  // ✅ FUNCIÓN: Verificar si ya existe un espacio con ese nombre (excluyendo el actual)
  const checkIfSpaceExists = (spaceName) => {
    if (!spaces || !Array.isArray(spaces)) return false;
    
    return spaces.some(s => 
      s.id !== space.id && // Excluir el espacio actual
      s.name.toLowerCase().trim() === spaceName.toLowerCase().trim()
    );
  };

  // ✅ FUNCIÓN: Actualizar todas las reservas que referencian este espacio
  const updateReservationsSpaceName = async (oldName, newName) => {
    try {
      console.log(`🔄 Actualizando reservas de "${oldName}" a "${newName}"`);
      
      // Buscar todas las reservas que referencian el nombre anterior del espacio
      const reservationsRef = collection(db, 'reservations');
      const q = query(reservationsRef, where('space', '==', oldName));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('✅ No se encontraron reservas para actualizar');
        return { updated: 0 };
      }

      // Usar batch para actualizar todas las reservas de una vez
      const batch = writeBatch(db);
      let updateCount = 0;

      querySnapshot.forEach((docSnapshot) => {
        const reservationRef = doc(db, 'reservations', docSnapshot.id);
        batch.update(reservationRef, { 
          space: newName,
          updatedAt: new Date().toISOString()
        });
        updateCount++;
      });

      // Ejecutar todas las actualizaciones
      await batch.commit();
      
      console.log(`✅ ${updateCount} reservas actualizadas exitosamente`);
      return { updated: updateCount };

    } catch (error) {
      console.error('❌ Error actualizando reservas:', error);
      throw new Error('Error al actualizar las reservas asociadas');
    }
  };

  // 🆕 FUNCIÓN: Manejar cambio de imagen
  const handleImageChange = (url) => {
    setImageUrl(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    const trimmedLocation = location.trim(); // 🆕 Trim de la ubicación
    
    if (trimmedName === '') {
      setError('El nombre del espacio es requerido');
      inputRef.current.focus();
      return;
    }

    // Verificar si hubo cambios
    const nameChanged = trimmedName !== space.name;
    const locationChanged = trimmedLocation !== (space.location || ''); // 🆕 Verificar cambio de ubicación
    const imageChanged = imageUrl !== (space.imageUrl || null);

    // Si no hay cambios, cerrar el modal
    if (!nameChanged && !locationChanged && !imageChanged) {
      onClose();
      return;
    }

    // ✅ VALIDACIÓN: Verificar duplicados (excluyendo el espacio actual)
    if (nameChanged && checkIfSpaceExists(trimmedName)) {
      setError('Ya existe un espacio con ese nombre. Por favor, elige un nombre diferente.');
      inputRef.current.focus();
      return;
    }

    setLoading(true);
    setError('');

    try {
      // ✅ PASO 1: Actualizar el espacio
      const spaceRef = doc(db, 'spaces', space.id);
      const updateData = {
        name: trimmedName,
        location: trimmedLocation || null, // 🆕 Actualizar ubicación
        imageUrl: imageUrl || null,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(spaceRef, updateData);

      // ✅ PASO 2: Actualizar todas las reservas si cambió el nombre
      let updateResult = { updated: 0 };
      if (nameChanged) {
        updateResult = await updateReservationsSpaceName(space.name, trimmedName);
      }
      
      console.log(`✅ Espacio actualizado. ${updateResult.updated} reservas actualizadas.`);

      // ✅ PASO 3: Notificar al componente padre
      onUpdate({ ...space, ...updateData });
      onClose();

    } catch (err) {
      console.error('Error al actualizar el espacio:', err);
      if (err.message.includes('reservas')) {
        setError('Error al actualizar las reservas asociadas. Por favor, inténtalo de nuevo.');
      } else {
        setError('Ha ocurrido un error al actualizar el espacio. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div 
        className={styles.modalContent}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className={styles.modalHeader}>
          <div className={styles.titleContainer}>
            {spaceType === 'spaces' ? (
              <Layout className={styles.titleIcon} />
            ) : (
              <Archive className={styles.titleIcon} />
            )}
            <h2 id="modal-title" className={styles.title}>
              {spaceType === 'spaces' ? 'Editar Espacio' : 'Editar Armario'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Cerrar modal"
          >
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="space-name" className={styles.label}>
              {spaceType === 'spaces' ? 'Nombre del espacio' : 'Nombre del armario'}
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="space-name"
                type="text"
                placeholder={spaceType === 'spaces' ? 'Ingresa el nuevo nombre del espacio' : 'Ingresa el nuevo nombre del armario'}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // Limpiar error cuando el usuario empiece a escribir
                  if (error) setError('');
                }}
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                ref={inputRef}
                maxLength={50}
                disabled={loading}
              />
              <span className={`${styles.characterCount} ${name.length > 40 ? styles.almostFull : ''}`}>
                {name.length}/50
              </span>
            </div>
            {error && (
              <div className={styles.error}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* 🆕 CAMPO DE UBICACIÓN */}
          <div className={styles.inputGroup}>
            <label htmlFor="space-location" className={styles.label}>
              Ubicación <span className={styles.optionalLabel}>(opcional)</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="space-location"
                type="text"
                placeholder="Ej: Piso 2, Edificio A, Sala 301"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={styles.input}
                maxLength={30}
                disabled={loading}
              />
              <span className={`${styles.characterCount} ${location.length > 25 ? styles.almostFull : ''}`}>
                {location.length}/30
              </span>
            </div>
          </div>

          {/* COMPONENTE DE SUBIDA DE IMAGEN */}
          <ImageUploader
            currentImage={imageUrl}
            onImageChange={handleImageChange}
            disabled={loading}
          />
        </form>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className={styles.spinnerIcon} />
                Actualizando...
              </>
            ) : (
              <>
                <Save />
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSpaceModal;